/* eslint-disable */




import { FRAMEWORK_SYNONYMS, TIME_PHRASE_MAP } from './dictionaries';

function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }

// ─── Policy ID ────────────────────────────────────────────────────────────────

const POLICY_ID_REGEX = /\b(?:P|POL)[- ]?(\d{1,8})\b/gi;

/**
 * Extract the first policy ID from a normalized string.
 * Normalizes to `P-XXXX` format.
 * NOTE: Works on the normalized (lowercase) string; regex is case-insensitive.
 */
export function extractPolicyId(normalized) {
    POLICY_ID_REGEX.lastIndex = 0;
    const match = POLICY_ID_REGEX.exec(normalized);
    if (!match) return undefined;
    const num = parseInt(match[1], 10);
    return `P-${num}`;
}

// ─── Policy Title ─────────────────────────────────────────────────────────────

// Patterns: "open policy <title>", "view policy named <title>", etc.
const POLICY_TITLE_PATTERNS = [
    /\bopen\s+policy\s+(?!P-|POL-)(.+)/i,
    /\bview\s+policy\s+(?!P-|POL-)(.+)/i,
    /\bshow\s+policy\s+(?!P-|POL-)(.+)/i,
    /\bedit\s+policy\s+(?!P-|POL-)(.+)/i,
    /\bopen\s+(?!P-|POL-|policies|templates)(.+?)\s+policy\b/i,
    /\bview\s+(?!P-|POL-|policies|templates)(.+?)\s+policy\b/i,
    /\bedit\s+(?!P-|POL-|policies|templates)(.+?)\s+policy\b/i,
    /\bopen\s+(?!P-|POL-|policies|templates|search|find|show|view|edit|list)(.+)/i,
    /\bedit\s+(?!P-|POL-|policies|templates|search|find|show|view|edit|list)(.+)/i,
    /\bview\s+(?!P-|POL-|policies|templates|search|find|show|view|edit|list)(.+)/i,
    /\bpolicy\s+named\s+(.+)/i,
    /\bpolicy\s+called\s+(.+)/i,
    // Allow 'approvals/acks/revisions for <title>' when no ID present
    /\b(?:approvals?|acks?|acknowledgements?)\s+for\s+(?!P-|POL-)([a-z][a-z0-9 ]{3,})/i,
];

/**
 * Extract a policy title when a user refers to a policy by name (not ID).
 * Only fires when no policy ID is found in the input.
 */
export function extractPolicyTitle(normalized, policyId) {
    if (policyId) return undefined; // ID takes precedence
    for (const pattern of POLICY_TITLE_PATTERNS) {
        const match = normalized.match(pattern);
        if (match && _optionalChain([match, 'access', _ => _[1], 'optionalAccess', _2 => _2.trim, 'call', _3 => _3(), 'access', _4 => _4.length]) >= 3) {
            const title = match[1].trim();
            // Guard: If it ends in "policies" (plural), it's a search, not a specific title
            if (title.endsWith('policies')) continue;

            // Trim common trailing words that are search noise
            return title.replace(/\s+(pdf|export|download|show|view)$/i, '').trim();
        }
    }
    return undefined;
}

// ─── Search Query ─────────────────────────────────────────────────────────────

const SEARCH_QUERY_PATTERNS = [
    /\bsearch(?:\s+policies?)?\s+(?:for\s+)?(.+)/i,
    /\bfind\s+policies?\s+(?:about|for|with|related\s+to)?\s*(.+)/i,
    /\bfind\s+(.+?)\s+policies?\b/i,  // "find access policies"
    /\bshow\s+policies?\s+(?:for\s+|with\s+|about\s+)?(.+)/i,
    /\bshow\s+(.+?)\s+policies?\b/i,  // "show access policies"
    /\blist\s+policies?\s+(?:for\s+|with\s+|about\s+)?(.+)/i,
    /\blist\s+(.+?)\s+policies?\b/i,  // "list access policies"
    /\bopen\s+(.+?)\s+policies?\b/i,  // "open access policies"
    /\bview\s+(.+?)\s+policies?\b/i,  // "view access policies"
    /\bhave\s+(.+?)\s+policies?\b/i,  // "do you have access policies"
    /\blookup\s+policies?\s+(?:for\s+)?(.+)/i,
    /\bexport\s+([a-z0-9 ]+?)\s+policies?\b/i, // "export visitor policies"
    /\/search\s+(.+)/i,
];

const SEARCH_STRIP_WORDS = /\s+(expiring|due|expire|next|last|this|in)\s+.+$/i;

export function extractSearchQuery(normalized, intent) {
    if (intent && !['policy_search', 'template_search'].includes(intent)) return undefined;
    for (const pattern of SEARCH_QUERY_PATTERNS) {
        const match = normalized.match(pattern);
        if (match && _optionalChain([match, 'access', _5 => _5[1], 'optionalAccess', _6 => _6.trim, 'call', _7 => _7(), 'access', _8 => _8.length]) >= 2) {
            const query = match[1].trim();
            // Guard: If it starts with 'templates', it's likely a template search, not policy search
            if (query.toLowerCase().startsWith('templates')) continue;

            // Strip time range tail from query
            return query.replace(SEARCH_STRIP_WORDS, '').trim() || undefined;
        }
    }
    return undefined;
}

// ─── Template Query ───────────────────────────────────────────────────────────

const TEMPLATE_QUERY_PATTERNS = [
    /\btemplates?\s+for\s+(.+)/i,
    /\bsearch\s+templates?\s+(?:for\s+)?(.+)/i,
    /\bfind\s+templates?\s+(?:for\s+|about\s+)?(.+)/i,
    /\bfind\s+(.+?)\s+templates?\b/i,  // "find access templates"
    /\bshow\s+templates?\s+for\s+(.+)/i,
    /\blist\s+templates?\s+(?:for\s+|about\s+)?(.+)/i,
    /\B\/draft\s+policy\s+(.+)/i, // "/draft policy visitor log"
    /\bdraft\s+(.+?)\s+polic(?:y|ies)\b/i,
    /\bcreate\s+polic(?:y|ies)\s+(?:for\s+|about\s+)?(.+)/i,
    /\bdraft\s+polic(?:y|ies)\s+(?:for\s+|about\s+)?(.+)/i,
    /\bnew\s+polic(?:y|ies)\s+(?:for\s+|about\s+)?(.+)/i,
    /\bcreate\s+(.+?)\s+polic(?:y|ies)\b/i,
    /\bnew\s+(.+?)\s+polic(?:y|ies)\b/i,
    /\bgenerate\s+polic(?:y|ies)\s+(?:for\s+|about\s+)?(.+)/i,
    /\bgenerate\s+(.+?)\s+polic(?:y|ies)\b/i,
    /\bhave\s+(.+?)\s+templates?\b/i,  // "do you have visitor log template"
];

export function extractTemplateQuery(normalized) {
    // Slash commands are not template titles — guard against e.g. "/search x templates" → template_query = "/search x"
    for (const pattern of TEMPLATE_QUERY_PATTERNS) {
        const match = normalized.match(pattern);
        if (match && _optionalChain([match, 'access', _9 => _9[1], 'optionalAccess', _10 => _10.trim, 'call', _11 => _11(), 'access', _12 => _12.length]) >= 2) {
            const query = match[1].trim();
            // Skip if captured text looks like a slash command
            if (query.startsWith('/')) continue;
            return query;
        }
    }
    return undefined;
}

// ─── Frameworks ───────────────────────────────────────────────────────────────

/**
 * Detect frameworks mentioned in the normalized input.
 * Returns only canonical IDs that are also in `orgEnabled` (if provided).
 * If `orgEnabled` is empty/undefined, returns all detected frameworks.
 */
export function extractFrameworks(
    normalized,
    tokens,
    orgEnabled = [],
) {
    const detected = new Set();

    for (const entry of FRAMEWORK_SYNONYMS) {
        for (const synonymGroup of entry.synonyms) {
            if (entry.requireAll !== false) {
                // All tokens in the group must be present
                const allPresent = synonymGroup.every(t =>
                    tokens.includes(t) || normalized.includes(t)
                );
                if (allPresent) {
                    detected.add(entry.canonical);
                    break;
                }
            } else {
                // Any token suffices
                const anyPresent = synonymGroup.some(t =>
                    tokens.includes(t) || normalized.includes(t)
                );
                if (anyPresent) {
                    detected.add(entry.canonical);
                    break;
                }
            }
        }
    }

    // Special: "cmmc" without level → could be L1 or L2 → ambiguous if both enabled
    const tokenStr = tokens.join(' ');
    const hasCmmcToken = tokens.includes('cmmc');
    const hasLevel1 = /l1|level.?1|cmmc1/.test(tokenStr);
    const hasLevel2 = /l2|level.?2|cmmc2/.test(tokenStr);
    const hasCmmcAmbiguity =
        hasCmmcToken &&
        !hasLevel1 &&
        !hasLevel2 &&
        orgEnabled.includes('CMMC_L1') &&
        orgEnabled.includes('CMMC_L2');

    // Filter to org-enabled frameworks if list is provided
    const filtered = orgEnabled.length > 0
        ? [...detected].filter(f => orgEnabled.includes(f))
        : [...detected];

    return {
        frameworks: [...new Set(filtered)],
        ambiguous: hasCmmcAmbiguity,
    };
}

// ─── Control IDs ──────────────────────────────────────────────────────────────

// CMMC control pattern: AC.L1-3.1.1
const CMMC_CONTROL_REGEX = /\b([A-Z]{1,4})\.L([12])-(\d+\.\d+\.\d+)\b/g;
// NIST SP 800-171 control pattern: 3.1.1 (only if NIST context hinted)
const NIST_CONTROL_REGEX = /\b([1-9]\d?)\.(\d+)\.(\d+)\b/g;
// ISO 27001 Annex A: A.9.1 or Annex A 9.1
const ISO_CONTROL_REGEX = /\bA\.(\d{1,2})\.(\d{1,2})\b/g;

export function extractControlIds(
    normalized,
    frameworks,
) {
    const results = [];
    const seen = new Set();

    // Uppercase the input for regex matching since control IDs are case-sensitive
    const upper = normalized.toUpperCase();

    const add = (raw, normalized_id, hint) => {
        if (!seen.has(normalized_id)) {
            seen.add(normalized_id);
            results.push({ raw, normalized: normalized_id, framework_hint: hint });
        }
    };

    // CMMC — match on uppercased string
    CMMC_CONTROL_REGEX.lastIndex = 0;
    let m;
    while ((m = CMMC_CONTROL_REGEX.exec(upper)) !== null) {
        const raw = m[0];
        const level = m[2];
        add(raw, raw.toUpperCase(), level === '1' ? 'CMMC_L1' : 'CMMC_L2');
    }

    // NIST (only when NIST or CMMC context hinted, to avoid false positives)
    const nistHinted = frameworks.some(f => f.startsWith('NIST') || f.startsWith('CMMC'));
    if (nistHinted || /nist/.test(normalized)) {
        NIST_CONTROL_REGEX.lastIndex = 0;
        while ((m = NIST_CONTROL_REGEX.exec(normalized)) !== null) {
            const raw = m[0];
            add(raw, raw, 'NIST_800_171');
        }
    }

    // ISO — match on uppercase
    const isoHinted = frameworks.includes('ISO_27001') || /iso|annex/.test(normalized);
    if (isoHinted) {
        ISO_CONTROL_REGEX.lastIndex = 0;
        while ((m = ISO_CONTROL_REGEX.exec(upper)) !== null) {
            const raw = m[0];
            add(raw, raw.toUpperCase(), 'ISO_27001');
        }
    }

    return results;
}

// ─── Time Range ───────────────────────────────────────────────────────────────

export function extractTimeRange(normalized) {
    for (const entry of TIME_PHRASE_MAP) {
        for (const pattern of entry.patterns) {
            const match = normalized.match(pattern);
            if (match) {
                return entry.extract(match);
            }
        }
    }
    return undefined;
}

// ─── Keywords (general) ───────────────────────────────────────────────────────

const KEYWORD_BLACKLIST = new Set([
    'policy', 'policies', 'template', 'templates', 'open', 'show', 'view',
    'edit', 'update', 'modify', 'go', 'list',
    'find', 'search', 'export', 'pdf', 'from', 'create', 'new',
    'help', 'revision', 'history', 'approval', 'approvals', 'acknowledgement',
    'acknowledgements', 'acks', 'mapping', 'mapped', 'framework',
]);

export function extractKeywords(tokens, frameworks, controlIds) {
    const frameworkTokens = new Set(frameworks.flatMap(f => f.toLowerCase().split('_')));
    const controlTokens = new Set(controlIds.flatMap(c => c.normalized.toLowerCase().split(/[.\s]/)));
    return tokens.filter(t =>
        !KEYWORD_BLACKLIST.has(t) &&
        !frameworkTokens.has(t) &&
        !controlTokens.has(t) &&
        t.length >= 2
    );
}
